import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, AlertTriangle, Clock, Zap, Users, Eye, CheckSquare, X, Plus } from 'lucide-react';
import api from '../utils/api';


const AITaskGenerationSettings = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await loadSettings();
      setLoading(false);
    };
    
    loadAllData();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/ai-task-generation/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('AI Settings Response:', response.data); // Debug log
      
      if (response.data.success) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load AI task generation settings:', error);
    }
  };





  const saveCompanySettings = async (companySettings) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await api.put(`/ai-task-generation/settings/company`, companySettings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      await loadSettings();
    } catch (error) {
      console.error('Failed to save company settings:', error);
    } finally {
      setSaving(false);
    }
  };





  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Zap className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">AI Task Generation Settings</h2>
          </div>
          <p className="text-gray-600 mt-1">
            Configure approval workflows and settings for AI-generated tasks from scope of work processing
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('company')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'company'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Company Defaults
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Company Settings Tab */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Company-wide AI Task Generation Settings</h3>
                <p className="text-gray-600 mb-6">
                  These settings apply to all clients by default. Client-specific overrides can be configured in individual Client Settings.
                </p>

                <div className="space-y-6">
                  {/* Auto Approval Settings */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Auto-Approval Settings</h4>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Require approval for all AI-generated tasks
                          </label>
                          <p className="text-xs text-gray-500">
                            When enabled, all AI-generated tasks will need manual approval before being created
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings?.company_settings?.require_approval_for_all !== false}
                          onChange={(e) => {
                            const newSettings = {
                              ...settings?.company_settings,
                              require_approval_for_all: e.target.checked
                            };
                            saveCompanySettings(newSettings);
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Auto-approve high confidence tasks
                          </label>
                          <p className="text-xs text-gray-500">
                            Automatically approve tasks that meet the confidence threshold
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings?.company_settings?.auto_approve_high_confidence || false}
                          onChange={(e) => {
                            const newSettings = {
                              ...settings?.company_settings,
                              auto_approve_high_confidence: e.target.checked
                            };
                            saveCompanySettings(newSettings);
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confidence Threshold ({Math.round((settings?.company_settings?.auto_approve_threshold || 0.85) * 100)}%)
                        </label>
                        <input
                          type="range"
                          min="0.5"
                          max="1.0"
                          step="0.05"
                          value={settings?.company_settings?.auto_approve_threshold || 0.85}
                          onChange={(e) => {
                            const newSettings = {
                              ...settings?.company_settings,
                              auto_approve_threshold: parseFloat(e.target.value)
                            };
                            saveCompanySettings(newSettings);
                          }}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Tasks with confidence above this threshold will be auto-approved
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                          Email notification when tasks are generated
                        </label>
                        <input
                          type="checkbox"
                          checked={settings?.company_settings?.notification_preferences?.email_on_generation !== false}
                          onChange={(e) => {
                            const newSettings = {
                              ...settings?.company_settings,
                              notification_preferences: {
                                ...settings?.company_settings?.notification_preferences,
                                email_on_generation: e.target.checked
                              }
                            };
                            saveCompanySettings(newSettings);
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                          Email notification when approval is needed
                        </label>
                        <input
                          type="checkbox"
                          checked={settings?.company_settings?.notification_preferences?.email_on_approval_needed !== false}
                          onChange={(e) => {
                            const newSettings = {
                              ...settings?.company_settings,
                              notification_preferences: {
                                ...settings?.company_settings?.notification_preferences,
                                email_on_approval_needed: e.target.checked
                              }
                            };
                            saveCompanySettings(newSettings);
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}



        </div>

        {saving && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
              <div className="animate-spin w-5 h-5 border-2 border-purple-200 border-t-purple-600 rounded-full"></div>
              <span className="text-gray-700">Saving settings...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AITaskGenerationSettings;